import * as React from 'react';
import { useState } from 'react';
import { RoleType, ENDORSEMENT_VALIDATION } from '@promo/shared';
import { Button } from '@/components/ui/button';
import { useSendInvitations, useInvitations } from '../hooks/useInvitations';
import { toast } from '@/hooks/use-toast';
import { FormField } from '@/components/FormField';
import {
  isValidTebraEmail,
  splitEmailTokens,
  TokenizedEmailAutocomplete,
} from './TokenizedEmailAutocomplete';

interface InvitationResult {
  success: { email: string; token?: string; acceptLink?: string }[];
  failed: { email: string; reason: string }[];
}

export interface InviteEndorserFormProps {
  packetId: string;
  onSuccess?: () => void;
}

export function InviteEndorserForm({ packetId, onSuccess }: InviteEndorserFormProps) {
  const [inviteeEmails, setInviteeEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [invalidEmailTokens, setInvalidEmailTokens] = useState<string[]>([]);
  const [validationError, setValidationError] = useState('');

  // Get existing invitations to enforce limits
  const { data: existingInvitations = [] } = useInvitations(packetId, RoleType.ENDORSER);
  const { mutate: sendInvitations, isPending } = useSendInvitations();

  const collectEmailsForSubmit = (): { validEmails: string[]; invalidEmails: string[] } => {
    const draftTokens = splitEmailTokens(emailDraft);
    const validEmails: string[] = [];
    const invalidEmails = [...invalidEmailTokens];

    [...inviteeEmails, ...draftTokens].forEach((email) => {
      const normalizedEmail = email.trim().toLowerCase();
      if (isValidTebraEmail(normalizedEmail)) {
        if (!validEmails.includes(normalizedEmail)) {
          validEmails.push(normalizedEmail);
        }
      } else {
        invalidEmails.push(email);
      }
    });

    return { validEmails, invalidEmails };
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    const { validEmails, invalidEmails } = collectEmailsForSubmit();

    if (invalidEmails.length > 0) {
      setValidationError(`Invalid email format or domain: ${invalidEmails.join(', ')}`);
      return;
    }

    if (validEmails.length === 0) {
      setValidationError('Please enter at least one valid email address.');
      return;
    }

    const totalInvitationCount = existingInvitations.length + validEmails.length;
    if (totalInvitationCount > ENDORSEMENT_VALIDATION.MAX_ENDORSERS) {
      setValidationError(
        `You can have at most ${ENDORSEMENT_VALIDATION.MAX_ENDORSERS} endorsers. You're trying to invite ${validEmails.length} more, which would exceed the limit.`
      );
      return;
    }

    sendInvitations(
      {
        packetId,
        inviteeEmails: validEmails,
        roleToInvite: RoleType.ENDORSER,
      },
      {
        onSuccess: (data: unknown) => {
          const result = (data ?? {
            success: validEmails.map((email) => ({ email })),
            failed: [],
          }) as InvitationResult;
          const successes = result.success ?? [];
          const failures = result.failed ?? [];

          if (failures.length > 0) {
            setValidationError(
              failures.map((failure) => `${failure.email}: ${failure.reason}`).join('; ')
            );
            setInviteeEmails(failures.map((failure) => failure.email.toLowerCase()));
            setEmailDraft('');
            setInvalidEmailTokens([]);
          }

          if (successes.length > 0) {
            toast({
              title: 'Invitations sent',
              description: `Invitations sent to ${successes.length} endorser${
                successes.length > 1 ? 's' : ''
              }.`,
            });
            if (failures.length === 0) {
              setInviteeEmails([]);
              setEmailDraft('');
              setInvalidEmailTokens([]);
              setValidationError('');
            }
            onSuccess?.();
          }
        },
        onError: (error) => {
          toast({
            title: 'Failed to send invitations',
            description: error instanceof Error ? error.message : 'An unknown error occurred',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-4 gap-4">
          <FormField htmlFor="emails" label="Emails" className="col-span-4">
            <TokenizedEmailAutocomplete
              id="emails"
              value={inviteeEmails}
              draftValue={emailDraft}
              onChange={setInviteeEmails}
              onDraftChange={setEmailDraft}
              onInvalidTokensChange={setInvalidEmailTokens}
              placeholder="email1@tebra.com, email2@tebra.com"
              disabled={isPending}
            />
            {validationError && <div className="text-sm text-destructive">{validationError}</div>}
          </FormField>
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Sending...' : 'Send Invitations'}
        </Button>
      </div>
    </form>
  );
}
